// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement | ShadowRoot} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/**
 * The focused element, looking through any open shadow roots.
 *
 * `document.activeElement` stops at a shadow host, so a trap over a shadow root would otherwise
 * never recognize its own first/last element and would never wrap.
 *
 * @returns {Element | null}
 */
function deepActiveElement() {
  let active = document.activeElement;
  while (active?.shadowRoot?.activeElement) active = active.shadowRoot.activeElement;
  return active;
}

/**
 * Trap focus within the given container.
 *
 * `container` may be a ShadowRoot, so that a trap can be placed over a third-party component that
 * renders into shadow DOM (see `upcart-a11y.js`).
 *
 * @param {HTMLElement | ShadowRoot} container - The container to trap focus within.
 */
export function trapFocus(container) {
  // Clean up any previously set traps.
  removeTrapFocus();

  if (!getFocusableElements(container).length) {
    // If nothing is focusable, just abort—no need to trap.
    return;
  }

  /**
   * Resolve the edges of the tab ring *on every keystroke*, never once at install time.
   * Trapped content is re-rendered while open, which detaches a cached `last` — the wrap then
   * never matches and focus escapes past the trap into the browser chrome.
   */
  const edges = () => {
    const focusable = getFocusableElements(container);
    return { first: focusable[0], last: focusable[focusable.length - 1] };
  };

  // Keydown handler for cycling focus with Tab and Shift+Tab
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(container);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;

    const activeEl = deepActiveElement();
    // Dialog panels often receive initial focus via tabindex="-1". They are inside the trap but not
    // in the tab-ring list, so Shift+Tab from them must wrap to `last` (not escape to chrome).
    const activeOutsideRing =
      activeEl instanceof Element &&
      activeEl !== first &&
      activeEl !== last &&
      !focusable.includes(/** @type {HTMLElement} */ (activeEl)) &&
      (container instanceof ShadowRoot
        ? container === activeEl || container.contains(activeEl)
        : container === activeEl || container.contains(activeEl));

    // If on the last focusable and tabbing forward, go to first
    if (!event.shiftKey && activeEl === last) {
      event.preventDefault();
      first.focus();
    }
    // If on the first (or the container / non-ring focus target) and shift-tabbing, go to last
    else if (event.shiftKey && (activeEl === first || activeEl === container || activeOutsideRing)) {
      event.preventDefault();
      last.focus();
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    // Test containment with composedPath(), not contains(): focus events crossing a shadow boundary
    // are retargeted to the host, so contains() reports "outside" for the container's own children.
    if (!event.composedPath().includes(container)) {
      event.stopPropagation();
      // E.g., refocus the first focusable element:
      edges().first?.focus();
    }
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  // Use capture phase for focusin so we can catch it before it lands outside
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  // Finally, put focus where you want it. A ShadowRoot isn't focusable, so enter at its first stop.
  if (container instanceof HTMLElement) container.focus();
  else edges().first?.focus();
}

/**
 * Make everything outside `element` inert, confining focus *and* the accessibility tree to it.
 *
 * A focus trap only holds the keyboard; `inert` is what also stops a screen reader's virtual cursor
 * from wandering into the page behind an overlay. Walks up to <body> inerting siblings at each
 * level, so it works for an overlay nested anywhere, not just a direct child of <body>.
 *
 * Siblings that were already inert are left alone, so the undo never un-inerts something we didn't set.
 *
 * @param {HTMLElement} element
 * @returns {() => void} Undo function.
 */
export function inertOutside(element) {
  /** @type {HTMLElement[]} */
  const inerted = [];

  for (let node = element; node && node !== document.body; node = node.parentElement) {
    const parent = node.parentElement;
    if (!parent) break;

    for (const sibling of parent.children) {
      if (sibling === node || !(sibling instanceof HTMLElement) || sibling.inert) continue;
      sibling.inert = true;
      inerted.push(sibling);
    }
  }

  return () => {
    for (const el of inerted) el.inert = false;
  };
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus(elementToFocus = null) {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
  // Restore focus to the trigger (e.g. the drawer's <summary>) so keyboard/AT users aren't
  // stranded at the top of the document when the overlay closes — the JSDoc already promised this.
  if (elementToFocus instanceof HTMLElement) elementToFocus.focus();
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
